import { useField } from "formik";

import {
  AddBoxRounded as AddBoxRoundedIcon,
  ArticleRounded as ArticleRoundedIcon,
  SaveAsRounded as SaveAsRoundedIcon,
} from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";

import { UserAnalyticsService } from "@analytics/index";

import TagInsertingEmailRichTextEditor from "@components/Forms/FormInputs/TagInsertingEmailRichTextEditor";
import ResourceLink from "@components/ResourceLink";

import C from "@constants/constants";

import { PopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

import TemplateCard from "./TemplateCard";

const templateCards = [
  {
    Icon: AddBoxRoundedIcon,
    id: 1,
    title: "Create New Template",
    tooltip: "Add an existing email template from your organization.",
  },
  {
    Icon: SaveAsRoundedIcon,
    id: 2,
    title: "Use Saved Template",
    tooltip: "Use a template saved from a previous campaign.",
  },
  {
    Icon: ArticleRoundedIcon,
    id: 3,
    title: "Use ScaleIP Template",
    tooltip: "Use a template provided by ScaleIP.",
  },
];

interface ICreateCampaignComposeContext {
  generateTemplate: (id: number) => Promise<void>;
}

export default function EditTemplate({ generateTemplate }: ICreateCampaignComposeContext) {
  const [emailTemplateField] = useField("emailTemplate");
  const editorVisible = !!emailTemplateField.value;

  const { savedCampaignTemplate } = useUserStore();

  const handleOpenResourceModal = () => {
    UserAnalyticsService.trackResourceLink(C.LOOM_TEMPLATES, "campaign");
    PopupActions.openModal({
      content: (
        <Box>
          <iframe
            allowFullScreen
            src={C.LOOM_TEMPLATES}
            style={{
              border: "none",
              height: "320px",
              width: "100%",
            }}
            title="Campaign Email Templates"
          />
          <Typography sx={{ mt: 3 }} variant="body1">
            <strong>You can use the following tags to personalize your email:</strong>
          </Typography>
          <Typography variant="body1">
            <ul>
              <li>
                <strong>[RECIPIENT_NAME]</strong> - The name of the recipient
              </li>
              <li>
                <strong>[YOUR_NAME]</strong> - Your first and last name
              </li>
              <li>
                <strong>[ORGANIZATION_NAME]</strong> - The name of your organization
              </li>
              <li>
                <strong>[COMPANY_NAME]</strong> - The company that the recipient works for
              </li>
              <li>
                <strong>[TECH_SUMMARY]</strong> - The technology summary provided in the Campaign setup
              </li>
              <li>
                <strong>[EMAIL_SIGNATURE]</strong> - Your email signature from the Account setup
              </li>
            </ul>
          </Typography>
        </Box>
      ),
      title: "Campaign Email Templates",
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        height: "100%",
        width: "100%",
      }}
    >
      {!editorVisible && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            padding: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              justifyContent: "center",
            }}
          >
            <Typography component="div" variant="h6">
              Choose Email Template
            </Typography>
            <ResourceLink customClickHandler={handleOpenResourceModal} workflow="create campaign" />
          </Box>
          <Grid
            alignItems="flex-start"
            container
            justifyContent="center"
            spacing={2}
            style={{ marginTop: 8, minHeight: "fit-content" }}
          >
            {templateCards.map(({ Icon, id, title, tooltip }) => {
              if (id !== 2 || !!savedCampaignTemplate) {
                return (
                  <TemplateCard
                    IconComponent={Icon}
                    key={id}
                    onClick={() => generateTemplate(id)}
                    title={title}
                    tooltip={tooltip}
                  />
                );
              }
            })}
          </Grid>
        </Box>
      )}
      {editorVisible && <TagInsertingEmailRichTextEditor handleOpenTutorialModal={handleOpenResourceModal} />}
    </Box>
  );
}
